// src/Pages/ExamPreview.jsx
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  Button,
  useTheme,
  Grid,
} from "@mui/material";
import SideBar from "../Component/SideBar";
import { fetchExamDetailsById } from "../Api/examApi";
import { useReactToPrint } from "react-to-print";

export default function ExamPreview() {
  const { id } = useParams();
  const theme = useTheme();

  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ Ref for printable content
  const printRef = useRef(null);

  // ✅ Print handler (new API requires contentRef)
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Exam Paper - ${
      exam?.generalInformation?.name || "Untitled"
    }`,
  });

  useEffect(() => {
    const loadExam = async () => {
      try {
        setLoading(true);
        const data = await fetchExamDetailsById(id);
        setExam(data);
      } catch (err) {
        console.error("Failed to fetch exam details:", err);
      } finally {
        setLoading(false);
      }
    };
    loadExam();
  }, [id]);

  return (
    <Box sx={{ display: "flex", flexDirection: "row" }}>
      {/* Sidebar (excluded from printing) */}
      <SideBar />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: "55px",
          backgroundColor:
            theme.palette.mode === "dark" ? "#1e1e1e" : "#fafafa",
          minHeight: "100vh",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="60vh"
          >
            <CircularProgress />
          </Box>
        ) : !exam ? (
          <Typography variant="h6" color="error">
            Failed to load exam details.
          </Typography>
        ) : (
          <>
            {/* ✅ Print Button */}
            {/* <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handlePrint}
                sx={{
                  "@media print": { display: "none" },
                }}
              >
                🖨️ Print Paper
              </Button>
            </Box> */}

            {/* ✅ Printable Section */}
            <div ref={printRef}>
              {/* Header Info */}
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  fontWeight="bold"
                  align="center"
                >
                  {exam.generalInformation?.name || "Untitled Test"}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography>
                      <strong>Subject :</strong>{" "}
                      {exam.generalInformation?.subject}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography>
                      <strong>Topic :</strong> {exam.generalInformation?.topic}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography>
                      <strong>Language :</strong>{" "}
                      {exam.generalInformation?.language}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography>
                      <strong>Test Date :</strong>{" "}
                      {new Date(
                        exam.generalInformation?.testDate
                      ).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography>
                      <strong>Valid Till :</strong>{" "}
                      {new Date(
                        exam.generalInformation?.validTillDate
                      ).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography>
                      <strong>Duration :</strong>{" "}
                      {exam.generalInformation?.durationInMinutes} mins
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography>
                      <strong>Total Questions :</strong>{" "}
                      {exam.generalInformation?.totalQuestions}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography>
                      <strong>Total Marks :</strong>{" "}
                      {exam.generalInformation?.totalQuestions *
                        exam.markingScheme?.marksPerQuestion}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Marking Scheme */}
              <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Marking Scheme
                </Typography>
                <Typography variant="body2">
                  📌 Marks/Question : {exam.markingScheme?.marksPerQuestion}{" "}
                  &nbsp; &nbsp; ✅ Correct:{" "}
                  {exam.markingScheme?.marksCorrectAnswer} &nbsp; &nbsp; ❌
                  Wrong : {exam.markingScheme?.marksWrongAnswer}
                </Typography>
              </Paper>
              {/* Instructions */}
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Instructions
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-line", color: "text.secondary" }}
                >
                  {exam.generalInformation?.instructions}
                </Typography>
              </Paper>
              {/* Questions */}
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Question Paper
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {exam.questionInformation?.questions?.map((q, index) => (
                  <Box key={q.id} mb={3} className="question">
                    <Typography
                      variant="body1"
                      component="div"
                      sx={{ display: "flex", gap: 1 }}
                    >
                      <span>{index + 1}.</span>
                      <span dangerouslySetInnerHTML={{ __html: q.question }} />
                    </Typography>
                    <Box pl={2} mt={1}>
                      {q.options.map((opt) => (
                        <Typography
                          key={opt.id}
                          variant="body2"
                          className="option"
                          sx={{ mb: 0.5 }}
                        >
                          {opt.id}. {opt.answer}
                        </Typography>
                      ))}
                    </Box>
                    {/* ✅ Explanation Section */}
                    {q.explanation && (
                      <Box mt={1} pl={2}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight="bold"
                        >
                          Explanation:
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="div"
                          dangerouslySetInnerHTML={{ __html: q.explanation }}
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </Paper>
            </div>
          </>
        )}
      </Box>
    </Box>
  );
}
